import { Component, Inject, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { FormBuilder, NgControl } from '@angular/forms';
import { IAngularMyDpOptions } from '@nodro7/angular-mydatepicker';
import { Subscription } from 'rxjs';
import { APP_CONFIG } from '../../../app.config';
import { AssistantPopoverService } from '../../../common/assistant-popover.service';
import { DateUtilsService } from '../../../common/date-utils.service';
import { DATEPICKER_CONFIG } from '../../../common/datepicker-config';
import { FixDateService } from '../../../common/fixDate.service';
import { ValidationService } from '../../../common/validation.service';
import { ValueListsService } from '../../../common/value-lists.service';
import { AppConfig } from '../../../models/config/app-config';
import { FormSubGroupControl } from '../../../models/formControls/FormSubGroupControl';
import { ValueListItem } from '../../../models/rest/valuelist/valuelist';
import { VisumErfassungSubFormComponent } from '../visum-erfassung-sub-form/visum-erfassung-sub-form.component';

@Component({
  selector: 'app-zusaetzliche-angaben',
  templateUrl: './zusaetzliche-angaben.component.html',
  styleUrls: ['./zusaetzliche-angaben.component.scss']
})
export class ZusaetzlicheAngabenComponent extends VisumErfassungSubFormComponent implements OnInit, OnDestroy {
  private rkvJaNeinSub: Subscription;
  private abholerSub: Subscription;
  private befoerderungsmittelSub: Subscription;
  befoerderungsmittelListItems: ValueListItem[];

  private begruedungFuerFehlendeReferenzListItemsSub: Subscription;
  begruedungFuerFehlendeReferenzListItems: ValueListItem[];

  private _referenzArt: string;
  private _rechtAufFreizuegigkeit: boolean;

  constructor(fb: FormBuilder,
              private valueListsService: ValueListsService,
              private validationService: ValidationService,
              @Inject(DATEPICKER_CONFIG) public datepickerOptions: IAngularMyDpOptions,
              fixDateService: FixDateService,
              dateUtils: DateUtilsService,
              assistantPopoverService: AssistantPopoverService,
              @Optional() @Self() controlDir: NgControl,
              @Inject(APP_CONFIG) public appConfig: AppConfig) {
    super(controlDir, assistantPopoverService);

    this.form = this.wrapFormGroup(fb.group({
      abweichendeSchreibweiseName: [null, validationService.getValidators('antragsteller.personendaten.abweichendeSchreibweiseName')],
      befoerderungsmittel: [null, validationService.getValidators('reisedaten.befoerderungsmittel')],
      rkvJaNein: null,//FE-only field
      rkvAnbieter: [null, validationService.getValidators('reisedaten.reisekrankenversicherung.daten.gesellschaft')],
      abholer: [null, validationService.getValidators('sonstiges.abholer')],
      familienname: [null, validationService.getValidators('sonstiges.familienname')],
      vorname: [null, validationService.getValidators('sonstiges.vorname')],
      anschrift: [null, validationService.getValidators('sonstiges.anschrift')],
      weitereAngaben: [null, validationService.getValidators('zusatzangaben.weitereAngaben')],
      begruedungFuerFehlendeReferenz: [null, validationService.getValidators('referenz.begruedungFuerFehlendeReferenz')],
      foermlicheVerpflichtungserklaerungDatum: [null, validationService.getValidators('referenz.foermlicheVerpflichtungserklaerung.datum', true)],
      stickernummer: new FormSubGroupControl()
    }));

    this.dateFixer = fixDateService.getConverter({ unknownReplacement: '00' });
    this.dateFormatter = dateUtils.getFormatter('yMd');
  }

  @Input()
  set referenzArt(referenzArt: string) {
    this._referenzArt = referenzArt;
    this.enableOrDisableBegruedungFuerFehlendeReferenz();
    this.enableOrDisableFoermlicheVerpflichtungserklaerungDatum();
  }

  @Input()
  set rechtAufFreizuegigkeit(rechtAufFreizuegigkeit: boolean) {
    this._rechtAufFreizuegigkeit = rechtAufFreizuegigkeit;
    this.enableOrDisableBegruedungFuerFehlendeReferenz();
    this.enableOrDisableFoermlicheVerpflichtungserklaerungDatum();
  }

  private enableOrDisableBegruedungFuerFehlendeReferenz(): void {
    if (this._referenzArt && this._referenzArt === '1' && !this._rechtAufFreizuegigkeit) {
      this.form.controls.begruedungFuerFehlendeReferenz.enable();
    } else {
      this.form.controls.begruedungFuerFehlendeReferenz.disable();
    }
  }

  private enableOrDisableFoermlicheVerpflichtungserklaerungDatum(): void {
    if (this._referenzArt && this._referenzArt !== '1' && !this._rechtAufFreizuegigkeit) {
      this.form.controls.foermlicheVerpflichtungserklaerungDatum.enable();
    } else {
      this.form.controls.foermlicheVerpflichtungserklaerungDatum.disable();
    }
  }


  onValidatorUpdate() {
    this.enableOrDisableRkvAnbieter(this.form.value.rkvJaNein);
    this.enableOrDisableAbholer(this.form.value.abholer);

    // alle felder in diesem Bereich sind keine Pflichtflder
    // der Bereich muss jedoch angefasst sein, damit er validiert wird
    this.form.controls.abweichendeSchreibweiseName.markAsTouched();
  }

  ngOnInit() {
    super.ngOnInit();

    this.form.controls.rkvJaNein.setValue(false);
    this.form.controls.abholer.setValue(false);

    this.befoerderungsmittelSub = this.valueListsService.getValueListItems('befoerderungsmittel')
    .subscribe((listItems: ValueListItem[]) => this.befoerderungsmittelListItems = listItems);

    this.begruedungFuerFehlendeReferenzListItemsSub = this.valueListsService.getValueListItems('begruendung_fehlende_referenz')
    .subscribe((listItems: ValueListItem[]) => this.begruedungFuerFehlendeReferenzListItems = listItems);


    this.rkvJaNeinSub = this.form.controls.rkvJaNein.valueChanges.subscribe(value => {
      if (this.form.value.rkvJaNein !== value) {
        this.enableOrDisableRkvAnbieter(value);
      }
    });

    this.abholerSub = this.form.controls.abholer.valueChanges.subscribe(value => {
      if (this.form.value.abholer !== value) {
        this.enableOrDisableAbholer(value);
      }
    });
  }

  ngOnDestroy(): void {
    this.rkvJaNeinSub.unsubscribe();
    this.abholerSub.unsubscribe();
    this.befoerderungsmittelSub.unsubscribe();
    this.begruedungFuerFehlendeReferenzListItemsSub.unsubscribe();
  }

  private enableOrDisableRkvAnbieter(rkvJaNein: boolean) {
    if (rkvJaNein) {
      this.form.controls.rkvAnbieter.enable();
    } else {
      this.form.controls.rkvAnbieter.disable();
    }
  }

  private enableOrDisableAbholer(abholer: boolean) {
    if (abholer) {
      this.form.controls.familienname.enable();
      this.form.controls.vorname.enable();
      this.form.controls.anschrift.enable();
    } else {
      this.form.controls.familienname.disable();
      this.form.controls.vorname.disable();
      this.form.controls.anschrift.disable();
    }
  }
}
